import {Dialog, Transition} from '@headlessui/react';
import React, {Fragment, ReactNode, useRef, useState} from 'react';
import {concatenate} from "services/string";
import Spinner from "components/Spinner";
import Button from "../Button";
import {Color} from "../Button/Button";

// Types ----

export enum ModalType { INFO, SUCCESS, WARNING, DANGER, ERROR }
export enum ModalSize { X_SMALL, SMALL, MEDIUM, LARGE, X_LARGE}

type ModalContainerProps = {
  type?: ModalType
  show: boolean
  approveLabel?: string
  onApprove?: () => Promise<any>
  approveDisabled?: boolean
  cancelLabel?: string
  onCancel?: () => any
  showCancel?: boolean
  size?: ModalSize
  children?: ReactNode
}

// Constants ----

const buttonColors: Record<ModalType, Color> = {
  [ModalType.INFO]: Button.colors.PRIMARY,
  [ModalType.SUCCESS]: Button.colors.SECONDARY,
  [ModalType.WARNING]: Button.colors.SECONDARY,
  [ModalType.DANGER]: Button.colors.RED,
  [ModalType.ERROR]: Button.colors.RED,
}

const modalSizeClasses: Record<ModalSize, string> = {
  [ModalSize.X_SMALL]: 'lg:max-w-lg',
  [ModalSize.SMALL]: 'lg:max-w-xl',
  [ModalSize.MEDIUM]: 'lg:max-w-2xl',
  [ModalSize.LARGE]: 'lg:max-w-4xl',
  [ModalSize.X_LARGE]: 'lg:max-w-7xl',
}

// Component ----

function ModalContainer({ type = ModalType.INFO, size = ModalSize.MEDIUM, show = true, approveLabel = 'OK', onApprove, cancelLabel = 'Cancel', onCancel, showCancel = true, children, approveDisabled }: ModalContainerProps) {
  const [handling, setHandling] = useState<boolean>(false);
  const cancelButtonRef = useRef(null);

  const hasActions = !!onApprove && !!onCancel;

  function handleCancel() {
    setHandling(false);
    onCancel?.();
  }

  function handleApprove() {
    setHandling(true);
    onApprove?.().then(() => setHandling(false));
  }

  return (
    <Transition show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={hasActions ? cancelButtonRef : undefined} onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          {/* Full-screen scrollable container */}
          <div className="fixed inset-0 w-screen overflow-y-auto">
            {/* Full-screen container to center the panel */}
            <div className="flex min-h-full items-center justify-center p-4">
              {/* The actual dialog panel  */}
              <Dialog.Panel className={concatenate(
                "mx-auto bg-white rounded-lg shadow-xl transform transition-all p-3 sm:p-6 w-full",
                modalSizeClasses[size]
              )}>
                {children}

                {/* Actions */}
                {(onApprove || (onCancel && showCancel)) &&
                  <div className="mt-6 sm:flex sm:flex-row-reverse gap-4">
                    {!!onApprove &&
                      <Button
                        type="button"
                        onClick={handleApprove}
                        // ref={cancelButtonRef}
                        disabled={handling || approveDisabled}
                        color={buttonColors[type]}
                      >
                        <div className="flex gap-2 items-center">
                          {handling? <Spinner color={Spinner.colors.LIGHT} size={Spinner.sizes.X_SMALL}/> : null}
                          {approveLabel}
                        </div>
                      </Button>
                    }

                    {(!!onCancel && showCancel) &&
                      <Button
                        type="button"
                        onClick={handleCancel}
                        // ref={cancelButtonRef}
                        disabled={handling}
                        color={Button.colors.WHITE}
                      >
                        {cancelLabel}
                      </Button>
                    }
                  </div>
                }
              </Dialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}

export { ModalContainer };